import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  inject,
} from "@angular/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Subscription } from "rxjs";
import { CoreService } from "src/app/services/core.service";
import {
  AppThemeMode,
  BrandingLayout,
  getTreesloLogoPath,
  resolveLogoThemeMode,
} from "./branding.model";

const MOBILE_VIEW = "screen and (max-width: 768px)";

@Component({
  selector: "app-branding",
  imports: [],
  templateUrl: "./branding.component.html",
  styleUrl: "./branding.component.scss",
})
export class BrandingComponent implements OnInit, OnDestroy {
  /** Sidebar: mini when collapsed on desktop/tablet only. Standalone: always large. */
  @Input() layout: BrandingLayout = "standalone";

  private readonly settings = inject(CoreService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly cdr = inject(ChangeDetectorRef);
  private layoutSubscription = Subscription.EMPTY;
  private settingsSubscription = Subscription.EMPTY;

  protected miniLogoSpinActive = false;

  private isMobileView = false;
  private wasSidenavCollapsed = false;

  ngOnInit(): void {
    this.wasSidenavCollapsed = this.settings.getOptions().sidenavCollapsed;

    this.settingsSubscription = this.settings.notify.subscribe(() => {
      this.onSettingsChanged();
      this.cdr.markForCheck();
    });

    this.layoutSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW])
      .subscribe((state) => {
        this.isMobileView = state.breakpoints[MOBILE_VIEW];
      });
  }

  ngOnDestroy(): void {
    this.settingsSubscription.unsubscribe();
    this.layoutSubscription.unsubscribe();
  }

  /** Mini logo only when sidenav is collapsed on desktop/tablet — never on mobile. */
  get useMiniLogo(): boolean {
    return (
      this.layout === "sidebar" &&
      this.settings.getOptions().sidenavCollapsed &&
      !this.isMobileView
    );
  }

  get isAuthLayout(): boolean {
    return this.layout === "auth";
  }

  get isMobileSidebarLarge(): boolean {
    return this.layout === "sidebar" && this.isMobileView && !this.useMiniLogo;
  }

  get isDesktopSidebarLarge(): boolean {
    return (
      this.layout === "sidebar" && !this.isMobileView && !this.useMiniLogo
    );
  }

  get themeMode(): AppThemeMode {
    return resolveLogoThemeMode(
      this.layout,
      this.settings.getOptions().theme,
    );
  }

  get logoSrc(): string {
    return getTreesloLogoPath(this.useMiniLogo ? "mini" : "large", this.themeMode);
  }

  get logoWidth(): number {
    if (this.useMiniLogo) {
      return 32;
    }
    if (this.layout === "auth") {
      return this.isMobileView ? 96 : 112;
    }
    if (this.isMobileSidebarLarge) {
      return 92;
    }
    if (this.layout === "sidebar") {
      return 112;
    }
    return 96;
  }

  protected onMiniLogoAnimationEnd(event: AnimationEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.animationName !== "treeslo-branding-mini-spin") {
      return;
    }

    this.miniLogoSpinActive = false;
    this.cdr.markForCheck();
  }

  private onSettingsChanged(): void {
    const collapsed = this.settings.getOptions().sidenavCollapsed;
    const enteredMiniSidebar =
      this.layout === "sidebar" &&
      !this.isMobileView &&
      collapsed &&
      !this.wasSidenavCollapsed;

    this.wasSidenavCollapsed = collapsed;

    if (enteredMiniSidebar) {
      this.triggerMiniLogoSpin();
    }
  }

  private triggerMiniLogoSpin(): void {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    this.miniLogoSpinActive = false;
    requestAnimationFrame(() => {
      this.miniLogoSpinActive = true;
      this.cdr.markForCheck();
    });
  }
}
